import type { ModuleId, PayeeLike } from '@tahi/core';
import { useData, useUsable } from './hooks';
import type { LocalDb } from './localDb';
import { useQuery } from './useQuery';

/** A payment method another module adds to Spending's quick add, e.g. a card 'card:<payeeId>'. */
export interface MethodOption {
  value: string;
  label: string;
  /**
   * v0.7.6: a credit card's billing days, so "From" can say which statement a purchase lands on
   * (purchaseCycle). Only card methods have it.
   */
  card?: Pick<PayeeLike, 'statementDay' | 'dueDays' | 'dueDaysAfterStatement'>;
}

/** Payments registers one (its cards), so Spending never imports Payments (conventions §18). */
export interface MethodProvider {
  id: string;
  moduleId: ModuleId;
  tables: readonly string[];
  load(db: LocalDb, userId: string): Promise<MethodOption[]>;
}

const providers = new Map<string, MethodProvider>();

export function registerMethodProvider(p: MethodProvider): void {
  providers.set(p.id, p);
}

/** Tests only. */
export function resetMethodProviders(): void {
  providers.clear();
}

/** Extra methods from every usable module's provider. */
export function useMethodOptions(): MethodOption[] {
  const { userId } = useData();
  const usable = useUsable();
  const list = [...providers.values()];
  const { data } = useQuery(
    list.flatMap((p) => [...p.tables]),
    async (db) => {
      const out: MethodOption[] = [];
      for (const p of list) if (usable(p.moduleId)) out.push(...(await p.load(db, userId)));
      return out;
    },
    [userId, usable, list.length],
  );
  return data ?? [];
}
